import { useEffect, useRef, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { Modal } from 'cpool-components/general/Modal';
import { Button } from 'cpool-components/input/Button';
import { ButtonVariant } from 'cpool-components/input/Button/types';
import { APPLICANT_ATTRIBUTIONS } from 'cpool-constants';
import type { ApplicantCreation, Schema_Wohnsituation } from 'cpool-schemas';
import type { Control } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LuCircleX, LuSave } from 'react-icons/lu';

import { Employment } from './content/Employment';
import { ExistingCredits } from './content/ExistingCredits';
import { IncomeAndExpenses } from './content/IncomeAndExpenses';
import { LivingSituation } from './content/LivingSituation';
import { PersonalData } from './content/PersonalData';
import { RealEstates } from './content/RealEstates';
import { TabTitleBar } from '~/features/leadDetails/components/Overview/CustomerData/shared/TabTitleBar';
import type { ApplicantModalTab } from './Tabs';
import { APPLICANT_MODAL_TABS, Tabs } from './Tabs';
import ApplicantAttributionContextProvider from './util/context/ApplicantAttributionContext';
import { getErrorCountsPerTab } from './util/getErrorCountsPerTab';
import { useWohnartValidation } from './util/hooks/useWohnartValidation';
import { type CreateApplicantFormValues, schemaCreateApplicantForm } from './util/schemaCreateApplicantForm';
import { onSubmit } from './util/submit/onSubmit';
import { defaultValues } from './util/values/defaultValues';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';

type Props = {
    open: boolean;
    onClose: () => void;
    onSelectTab?: (tab: ApplicantModalTab) => void;
    leadId: string;
    mainApplicantWohnsituation: Schema_Wohnsituation;
    selectedTab?: ApplicantModalTab;
};

export const CreateApplicantModal: React.FC<Props> = ({
    leadId,
    mainApplicantWohnsituation,
    onClose,
    onSelectTab,
    open,
    selectedTab,
}) => {
    const { t } = useTranslation();
    const [submitting, setSubmitting] = useState(false);
    const [internalSelectedTab, setInternalSelectedTab] = useState<ApplicantModalTab>(
        APPLICANT_MODAL_TABS.PERSONAL_DATA,
    );
    const activeTab = selectedTab ?? internalSelectedTab;
    const keepFormDataRef = useRef<CreateApplicantFormValues | null>(null);
    const [sameHouseholdValues, setSameHouseholdValues] = useState<CreateApplicantFormValues['wohnsituation'] | null>(
        null,
    );
    const { permissions } = useAclPermissions({ loanRequestId: leadId });
    const isCreateApplicantRoutineDisabled = !permissions.api.createApplicantRoutine;

    const methods = useForm<CreateApplicantFormValues>({
        defaultValues,
        mode: 'onChange',
        resolver: zodResolver(schemaCreateApplicantForm),
    });
    const {
        control,
        formState: { errors },
        getValues,
        handleSubmit,
        reset,
    } = methods;

    const errorCounts = getErrorCountsPerTab(errors);
    const { validationError: wohnartValidationError } = useWohnartValidation({
        control: control as Control<ApplicantCreation>,
    });

    useEffect(() => {
        if (open && keepFormDataRef.current) reset(keepFormDataRef.current, { keepDirty: true, keepDirtyValues: true });
    }, [open, reset]);

    const handleClose = () => {
        keepFormDataRef.current = getValues();
        onClose();
    };

    const changeTab = (tab: ApplicantModalTab) => {
        if (selectedTab === undefined) setInternalSelectedTab(tab);
        onSelectTab?.(tab);
    };
    const isTabVisible = (tab: ApplicantModalTab) => activeTab === tab;

    return (
        <Modal
            open={open}
            onClose={handleClose}
            title={t('CREDIT_WORTHINESS.BUTTONS.ADD_APPLICANT')}
            variant="sidebar"
            width="1100px"
            side="right"
        >
            <ApplicantAttributionContextProvider attribution={APPLICANT_ATTRIBUTIONS.ANTRAGSTELLER_2}>
                <FormProvider {...methods}>
                    <form
                        onSubmit={handleSubmit((data) =>
                            onSubmit({ aggregateId: leadId, onClose, reset, setSubmitting })(data as ApplicantCreation),
                        )}
                        className="flex flex-col flex-1 min-h-0 pt-4"
                    >
                        <div className="flex min-h-0 flex-1 flex-col gap-3 rounded-md bg-surface-light p-6">
                            <Tabs
                                selectedTab={activeTab}
                                onSelectTab={changeTab}
                                errorCounts={errorCounts}
                                wohnartValidationError={wohnartValidationError}
                            />
                            <TabTitleBar text={t(`CUSTOMER_DATA_TABS.${activeTab}`)} />
                            <div className="flex w-full flex-1 flex-col gap-2 overflow-y-auto">
                                <div
                                    className="w-full"
                                    style={{
                                        display: isTabVisible(APPLICANT_MODAL_TABS.PERSONAL_DATA) ? 'block' : 'none',
                                    }}
                                >
                                    <PersonalData />
                                </div>
                                <div
                                    className="w-full"
                                    style={{
                                        display: isTabVisible(APPLICANT_MODAL_TABS.INCOME_AND_EXPENSES)
                                            ? 'block'
                                            : 'none',
                                    }}
                                >
                                    <IncomeAndExpenses />
                                </div>
                                <div
                                    className="w-full"
                                    style={{
                                        display: isTabVisible(APPLICANT_MODAL_TABS.LIVING_SITUATION) ? 'block' : 'none',
                                    }}
                                >
                                    <LivingSituation
                                        mainApplicantWohnsituation={mainApplicantWohnsituation}
                                        sameHouseholdValues={sameHouseholdValues}
                                        onSaveSameHouseholdValues={setSameHouseholdValues}
                                    />
                                </div>
                                <div
                                    className="w-full"
                                    style={{
                                        display: isTabVisible(APPLICANT_MODAL_TABS.REAL_ESTATE) ? 'block' : 'none',
                                    }}
                                >
                                    <RealEstates />
                                </div>
                                <div
                                    className="w-full"
                                    style={{
                                        display: isTabVisible(APPLICANT_MODAL_TABS.EMPLOYMENT) ? 'block' : 'none',
                                    }}
                                >
                                    <Employment />
                                </div>
                                <div
                                    className="w-full"
                                    style={{
                                        display: isTabVisible(APPLICANT_MODAL_TABS.EXISTING_CREDITS) ? 'block' : 'none',
                                    }}
                                >
                                    <ExistingCredits />
                                </div>
                            </div>
                        </div>
                        <div className="mt-4 flex justify-end gap-2">
                            <Button
                                type="button"
                                variant={ButtonVariant.SECONDARY}
                                iconLeft={<LuCircleX size={14} />}
                                onClick={handleClose}
                                disabled={submitting}
                            >
                                {t('BUTTONS.CANCEL')}
                            </Button>
                            <Button
                                type="submit"
                                variant={ButtonVariant.PRIMARY}
                                disabled={submitting || isCreateApplicantRoutineDisabled}
                                iconLeft={<LuSave size={15} />}
                            >
                                {submitting ? `${t('BUTTONS.SAVE')}...` : t('BUTTONS.SAVE')}
                            </Button>
                        </div>
                    </form>
                </FormProvider>
            </ApplicantAttributionContextProvider>
        </Modal>
    );
};
